import React, { useEffect, useState } from 'react';
import { Button, Colors, Text, View } from 'react-native-ui-lib';
import { Image, Platform } from 'react-native';
import { FlatList } from 'react-native-gesture-handler';
import { collection, getDocs, getFirestore, limit, orderBy, query as firestoreQuery } from 'firebase/firestore';
import { getFeedElementsQuery, useLazyElementsLoader } from '../firebaseWrapper/firebaseWrapper';
import { ContainerType, FirestoreConstants, PathConstants, navigationProps } from '../typings/types';
import { List, TopicListPublic } from './StatementList';
import { StatementView } from './StatementView';
import { Statement } from './Containers';
import { strings } from '../localization/localization.web';
import { useIsMobile } from '../MediaQueries';
import { Dropdown } from './Dropdown';
import { NothingHereInformation } from './Alert';
import { Footer } from './Footer';
import { convertPathToInfo, getIdFromPath, getLocationFromPath } from '../helperFunctions';
import { showLinkDialog } from './Dialogs';
import { GeneratedBadge } from './Badges';
import { CoursesTeaser } from './CoursesTeaser';
import exampleArticleImage1 from '../../assets/res/exampleArticleImage1.png';
import exampleArticleImage2 from '../../assets/res/exampleArticleImage2.png';
import exampleArticleImage3 from '../../assets/res/exampleArticleImage3.png';
import { AntDesign } from '@expo/vector-icons';

const FEED_LOAD_INCREMENT = 10;

type StatementFeedItem = {
  itemType: 'statement';
  path: string;
  creationTime: any;
};

type SourceFeedItem = {
  itemType: 'source';
  id: string;
  creationTime: any;
  url?: string;
  originalTitle?: string;
  mainRelatedStatementsIds?: string[];
};

type FeedItem = StatementFeedItem | SourceFeedItem;

type FeedFilter = 'all' | 'arguments' | 'sources';

function getDomain(url: string): string {
  if (!url) {
    return '';
  }
  const regex = /^(?:https?:\/\/)?(?:www\.)?([^\/]+)/;
  const match = url.match(regex);
  return match ? match[1] : null;
}

function getRandomExampleImage(): any {
  const images = [exampleArticleImage1, exampleArticleImage2, exampleArticleImage3];
  return images[Math.floor(Math.random() * images.length)];
}

function creationTimeMillis(item: FeedItem): number {
  return item.creationTime?.toMillis ? item.creationTime.toMillis() : (item.creationTime?.seconds ?? 0) * 1000;
}

// The Sources feature (news-article-derived posts, see FirestoreConstants.SOURCES) only exists
// on web -- there is no `sources` support in firebaseWrapper.native.ts. This component is used
// by every platform (see PublicScreen.tsx), so the source-fetching effect and the merged
// rendering below are gated on Platform.OS === 'web'; native keeps rendering the statement-only
// feed it always has, via the untouched LazyList path.
export function FeedList({ navigation, route }: navigationProps): JSX.Element {

  const isMobile = useIsMobile();
  const isWeb = Platform.OS === 'web';

  const [items, setItems] = useState<FeedItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [entryLimit, setEntryLimit] = useState(FEED_LOAD_INCREMENT);
  const [filter, setFilter] = useState<FeedFilter>('all');

  const filterOptions: { label: string; value: FeedFilter }[] = [
    { label: strings.feed_filter_all, value: 'all' },
    { label: strings.arguments, value: 'arguments' },
    { label: strings.sources, value: 'sources' },
  ];
  const activeFilterIndex = filterOptions.findIndex(option => option.value === filter);

  useEffect(() => {
    if (!isWeb) {
      return;
    }
    setLoading(true);
    Promise.all([
      getDocs(getFeedElementsQuery(entryLimit)).then(snapshot =>
        snapshot.docs
          // Malformed/legacy docs missing their own `path` field would otherwise crash
          // getLocationFromPath()/getIdFromPath() at render time and take down the whole feed
          // (see #36 for the equivalent issue with a malformed `sources` doc).
          .filter(doc => !!doc.data().path)
          .map((doc): StatementFeedItem => ({
            itemType: 'statement',
            path: doc.data().path,
            creationTime: doc.data().creationTime,
          }))
      ),
      getDocs(firestoreQuery(collection(getFirestore(), FirestoreConstants.SOURCES), orderBy('creationTime', 'desc'), limit(entryLimit))).then(snapshot =>
        snapshot.docs.map((doc): SourceFeedItem => ({
          itemType: 'source',
          id: doc.id,
          ...(doc.data() as Omit<SourceFeedItem, 'itemType' | 'id'>),
        }))
      ),
    ]).then(([statementItems, sourceItems]) => {
      const merged = [...statementItems, ...sourceItems].sort((a, b) => creationTimeMillis(b) - creationTimeMillis(a));
      setItems(merged);
      setLoading(false);
    });
  }, [entryLimit, isWeb]);

  if (!isWeb) {
    return (
      <View
        flex
        style={{
          backgroundColor: Colors.primaryBG
        }}
      >
        <View flex
          style={{
            backgroundColor: isMobile ? 'transparent' : Colors.secondaryBG,
            borderRadius: 20,
            marginHorizontal: isMobile ? 0 : 5,
            marginVertical: 5,
            overflow: 'hidden',
          }}
        >
          <View style={{ marginLeft: isMobile ? 10 : 25, marginTop: isMobile ? 0 : 20 }}>
            <TopicListPublic navigation={navigation} header={strings.topics} />
          </View>
          <LazyList
            navigation={navigation}
            route={route}
            query={getFeedElementsQuery()}
            location={PathConstants.PUBLIC}
            showStatementView={true}
            // Rendered as the list's own scrollable header (rather than a fixed element
            // positioned above the list) so it scrolls away with the rest of the feed.
            extraHeader={
              <View style={{ marginBottom: 10 }}>
                <CoursesTeaser navigation={navigation} testID="feed-courses-teaser" />
              </View>
            }
          />
        </View>
      </View>
    );
  }

  const filteredItems = items.filter(item => {
    if (filter === 'arguments') {
      return item.itemType === 'statement';
    } else if (filter === 'sources') {
      return item.itemType === 'source';
    }
    return true;
  });

  return (
    <View
      flex
      style={{
        backgroundColor: Colors.primaryBG
      }}
    >
      <View flex
        style={{
          backgroundColor: isMobile ? 'transparent' : Colors.secondaryBG,
          borderRadius: 20,
          marginHorizontal: isMobile ? 0 : 5,
          marginVertical: 5,
          overflow: 'hidden',
        }}
      >
        <View style={{ marginLeft: isMobile ? 10 : 25, marginTop: isMobile ? 0 : 20 }}>
          <TopicListPublic navigation={navigation} header={strings.topics} />
        </View>
        <View style={{
          flex: 1,
          backgroundColor: 'transparent',
          borderRadius: 20,
          margin: 0,
          marginHorizontal: isMobile ? 0 : 20,
          marginBottom: 0,
          overflow: 'hidden',
          paddingHorizontal: isMobile ? 10 : 0
        }}>
          <FlatList
            testID="feed-list"
            data={filteredItems}
            keyExtractor={(item: FeedItem) => item.itemType === 'statement' ? item.path : `source-${item.id}`}
            ListHeaderComponent={
              <>
                <View row style={{ justifyContent: 'flex-end', marginBottom: 10 }}>
                  <View testID="feed-filter-dropdown">
                    <Dropdown
                      options={filterOptions.map(option => option.label)}
                      optionTestIDs={filterOptions.map(option => `feed-filter-option-${option.value}`)}
                      activeIndex={activeFilterIndex}
                      setActiveIndex={(index) => setFilter(filterOptions[index].value)}
                    />
                  </View>
                </View>
                {/* Part of the list's own scrollable header, below the filter row, so it
                    scrolls away with the rest of the feed instead of staying pinned above it. */}
                <View style={{ marginBottom: 10 }}>
                  <CoursesTeaser navigation={navigation} testID="feed-courses-teaser" />
                </View>
              </>
            }
            ListEmptyComponent={!loading ? <NothingHereInformation text={strings.no_statements_info} /> : null}
            ListFooterComponent={filteredItems.length > 0 ? <Footer navigation={navigation} /> : null}
            onEndReached={() => {
              if (!loading) {
                setEntryLimit(entryLimit + FEED_LOAD_INCREMENT);
              }
            }}
            onEndReachedThreshold={0.5}
            showsVerticalScrollIndicator={false}
            ItemSeparatorComponent={() => <View padding-5 />}
            renderItem={({ item }: { item: FeedItem }) => (
              item.itemType === 'statement' ?
                <View testID={`feed-statement-item-${item.path}`} style={{ marginBottom: 10, marginHorizontal: isMobile ? 0 : 5 }}>
                  <StatementView
                    navigation={navigation}
                    location={getLocationFromPath(item.path)}
                    statementId={getIdFromPath(item.path)}
                  />
                </View>
                :
                <View testID={`source-item-${item.id}`} style={{ marginHorizontal: 0, marginVertical: 0, marginBottom: 10 }}>
                  <View style={{ backgroundColor: 'transparent', borderRadius: 20, padding: 0 }}>
                    <Image
                      source={getRandomExampleImage()}
                      style={{
                        width: '100%',
                        height: isMobile ? 200 : 350,
                        borderRadius: 10, opacity: 1,
                      }}
                    />
                    <Text marginT-1 marginB-1 textSecondary text70>
                      {strings.extracted_from + " "}
                      <Button link onPress={() => showLinkDialog(item.url, strings)}>
                        <Text textSecondary text70>
                          {getDomain(item.url)}
                        </Text>
                      </Button>
                    </Text>
                    <Text marginB-2 text50 style={{ color: Colors.textPrimary }}>
                      {item.originalTitle}
                      {" "}
                      <GeneratedBadge />
                    </Text>
                    <View>
                      {item.mainRelatedStatementsIds ?
                        <FlatList
                          data={item.mainRelatedStatementsIds}
                          keyExtractor={(path: string) => path}
                          renderItem={({ item: statementPath }) => (
                            <Statement
                              info={convertPathToInfo(statementPath)}
                              parentInfo={route.params?.parentInfo || ''}
                              navigation={navigation}
                              showHeader={false}
                              lightweight
                            />
                          )}
                          ItemSeparatorComponent={() => <View padding-2 />}
                          ListFooterComponent={() =>
                            <View row right>
                              <Button
                                link
                                testID={`source-view-all-${item.id}`}
                                onPress={() => {
                                  navigation.push("source", { loc: "public", id: item.id, type: "source" });
                                }}
                              >
                                <Text textSecondary text80 textAlign='center'>
                                  {strings.all_arguments}
                                  <AntDesign name="arrowright" size={12} color={Colors.textSecondary} />
                                </Text>
                              </Button>
                            </View>
                          }
                        />
                        : null
                      }
                    </View>
                  </View>
                </View>
            )}
          />
        </View>
      </View>
    </View>
  );
}

type LazyListProps = {
  navigation: any;
  route: any;
  itemType?: ContainerType;
  query: any;
  location: PathConstants;
  groupId?: string;
  memberId?: string;
  groupAdminLoggedIn?: boolean;
  header?: string;
  showStatementView?: boolean;
  // Extra content rendered as part of the list's own scrollable header (after its built-in
  // header row), so callers can add content that scrolls away with the rest of the list
  // instead of pinning it above the list.
  extraHeader?: React.ReactNode;
  // Forwarded to List -- see its addAction for when a caller should set this.
  addAction?: { label: string; onPress: () => void; testID?: string };
};

export function LazyList({
  navigation,
  route,
  itemType,
  query,
  location,
  groupId,
  memberId,
  groupAdminLoggedIn,
  header,
  showStatementView = false,
  extraHeader,
  addAction,
}: LazyListProps): JSX.Element {
  const parentInfo = location === PathConstants.GROUP ?
    {
      loc: PathConstants.GROUP,
      id: groupId,
      type: ContainerType.GROUP,
      groupId: groupId

    }
    :
    route.params?.parentInfo || '';
  const {
    infos,
    loading,
    error,
    refresh,
    loadMoreElements: loadMoreElements,
  } = useLazyElementsLoader(query, parentInfo, location, groupId);

  if (error) {
    return (
      <View flex center>
        <Text>Error: {error.message}</Text>
      </View>
    );
  }

  const loadMoreEntriesWrapper = () => {
    loadMoreElements(10);
  };

  return (
    <>
      {infos && (
        <List
          itemType={itemType}
          infoList={infos}
          parentInfo={parentInfo}
          navigation={navigation}
          loading={loading}
          onRefresh={refresh}
          onEndReached={loadMoreEntriesWrapper}
          header={header}
          groupId={groupId}
          memberId={memberId}
          groupAdminLoggedIn={groupAdminLoggedIn}
          showStatementView={showStatementView}
          extraHeader={extraHeader}
          addAction={addAction}
        />
      )}
    </>
  );
}
